import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ColorPicker, InputWithLabel, Text, ToastContext } from '@reveni-io/reveni-ui'
import { Divider } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import AppContext from '@/common/context/app.context'
import routes from '@/Router/routes.constants'

import GroupDetailConfigurationSkeleton from './GroupDetailConfigurationSkeleton/GroupDetailConfigurationSkeleton'
import useGroup from './hooks/useGroup'
import PermissionsGroups from './PermissionsGroups/PermissionsGroups'
import { checkAndAddRequiredPermissions, getGroupPayload } from './helpers'
import * as Styles from './styles'

const GroupDetailConfiguration = () => {
  const { id } = useParams()
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const navigate = useNavigate()

  const {
    name,
    setName,
    color,
    setColor,
    permissions,
    setPermissions,
    usersCount,
    hasChanges,
    setHasChanges,
    revertChanges,
    createUserGroup,
    creatingUserGroup,
    editUserGroup,
    editingUserGroup,
    availablePermissions,
    loadingGroup,
    backendPermissions,
    defaultGroup,
  } = useGroup({
    storeId: info?.selectedStore?.id,
    groupId: id,
    allowsShipments: info?.selectedStore?.preferences?.allows_shipments,
  })

  const onSave = async () => {
    const payload = getGroupPayload({ id, name, color, permissions, backendPermissions })

    if (id === 'new') {
      try {
        await createUserGroup({ storeId: info.selectedStore.id, group: payload })
        setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
        navigate(routes.configurationPage.users)
      } catch (error) {
        setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      }
    } else {
      try {
        await editUserGroup({ storeId: info.selectedStore.id, group: payload })
        setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
        setHasChanges(false)
      } catch (error) {
        setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      }
    }
  }

  const handlePermissionsChange = permissions => {
    setPermissions(checkAndAddRequiredPermissions(permissions))
  }

  if (loadingGroup) {
    return <GroupDetailConfigurationSkeleton />
  }

  return (
    <>
      <Styles.DetailsContainer>
        {hasChanges && <UnsavedChangesAlert />}
        <Text type="titleM">{t('Detalles')}</Text>
        <InputWithLabel
          label={t('Nombre del Grupo')}
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={defaultGroup}
          fullWidth
        />
        <ColorPicker
          label={t('Elige un color para el grupo')}
          defaultColor={color}
          onChange={color => setColor(color)}
        />
        <Styles.UsersCountContainer>
          <Text type="bodyMBold">{t('Número de miembros')}:</Text>
          <Text type="bodyMRegular">{usersCount}</Text>
        </Styles.UsersCountContainer>
      </Styles.DetailsContainer>
      {!defaultGroup && (
        <>
          <Divider />
          <Styles.PermissionsContainer>
            <Text type="titleM">{t('Permisos')}</Text>
            <Text type="bodyMRegular">
              {t('Define los permisos para cada página que tendrán los usuarios que pertenezcan a este grupo:')}
            </Text>
            <PermissionsGroups
              availablePermissions={availablePermissions}
              permissions={permissions}
              handlePermissionsChange={handlePermissionsChange}
            />
          </Styles.PermissionsContainer>
        </>
      )}
      <SaveChangesButtons
        hasChanges={hasChanges}
        onSave={onSave}
        onRevertChanges={revertChanges}
        loading={creatingUserGroup || editingUserGroup}
        disabled={creatingUserGroup || editingUserGroup}
      />
    </>
  )
}

export default GroupDetailConfiguration
